
import React from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Calendar, Gift, ShoppingBag, DollarSign } from "lucide-react";
import { useRedemptionStats } from "@/hooks/useRedemptionStats";
import { RedeemedItem } from "@/types/redemptionTypes";

// Temporary: local mock data for demonstration. Replace with server data in production.
const mockRedemptions: RedeemedItem[] = [
  {
    id: "1",
    userId: "1",
    username: "user1",
    email: "user1@example.com",
    date: "2025-04-02",
    type: "CASH",
    amount: 100,
    paymentMethod: "ACH",
    status: "COMPLETED",
    pointValueAtRedemption: 100,
  },
  {
    id: "2",
    userId: "2",
    username: "user2",
    email: "user2@example.com",
    date: "2025-04-01",
    type: "GIFT_CARD",
    amount: 60,
    code: "GC-84884922",
    status: "COMPLETED",
    pointValueAtRedemption: 60,
  },
  {
    id: "3",
    userId: "3",
    username: "user3",
    email: "user3@example.com",
    date: "2025-03-12",
    type: "PROMO_CODE",
    amount: 25,
    code: "PRM-1111222",
    status: "COMPLETED",
    pointValueAtRedemption: 25,
  },
  {
    id: "4",
    userId: "4",
    username: "user4",
    email: "user4@example.com",
    date: "2025-04-10",
    type: "CASH",
    amount: 80,
    paymentMethod: "PAYPAL",
    status: "PENDING",
    pointValueAtRedemption: 80,
  },
];

function getMonthAndYear() {
  const now = new Date();
  return { month: now.getMonth(), year: now.getFullYear() };
}

function summarizeRedemptions(redemptions: RedeemedItem[]) {
  const { month, year } = getMonthAndYear();
  let monthTotal = 0;
  let ytdTotal = 0;
  const methodTotals: Record<string, number> = {};

  redemptions.forEach((r) => {
    const rdate = new Date(r.date);
    if (r.status !== "COMPLETED") return; // Only count completed
    if (rdate.getFullYear() === year) {
      ytdTotal += r.amount;
      if (rdate.getMonth() === month) {
        monthTotal += r.amount;
      }
    }
    const method = r.type;
    methodTotals[method] = (methodTotals[method] ?? 0) + r.amount;
  });

  return { monthTotal, ytdTotal, methodTotals };
}

const PointsRedemptionReport: React.FC = () => {
  const redemptionStats = useRedemptionStats();
  const summary = summarizeRedemptions(mockRedemptions);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "CASH":
        return <DollarSign className="h-4 w-4" />;
      case "GIFT_CARD":
        return <Gift className="h-4 w-4" />;
      case "PROMO_CODE":
        return <ShoppingBag className="h-4 w-4" />;
      default:
        return <DollarSign className="h-4 w-4" />;
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return <Badge variant="success">Completed</Badge>;
      case "PENDING":
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-200">
            Pending
          </Badge>
        );
      case "FAILED":
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Points Redemption Report</CardTitle>
        <CardDescription>
          Overview of redeemed points by users and breakdown by redemption method.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Stats Section */}
        <div className="flex flex-col md:flex-row md:space-x-8 mb-6">
          <div className="flex-grow mb-4 md:mb-0">
            <div className="flex flex-col gap-2">
              <span className="font-semibold text-muted-foreground">Points Redeemed (This Month)</span>
              <span className="text-2xl font-bold text-blue-700">{summary.monthTotal}</span>
            </div>
            <div className="flex flex-col gap-2 mt-2">
              <span className="font-semibold text-muted-foreground">Year-To-Date</span>
              <span className="text-2xl font-bold text-green-700">{summary.ytdTotal}</span>
            </div>
          </div>
          <div className="flex flex-col space-y-2">
            <span className="font-semibold text-muted-foreground mb-1">By Method</span>
            <div className="flex flex-wrap gap-3">
              <Badge className="bg-gray-200 text-gray-900">
                Cash: {summary.methodTotals["CASH"] || 0}
              </Badge>
              <Badge className="bg-gray-200 text-gray-900">
                Gift Card: {summary.methodTotals["GIFT_CARD"] || 0}
              </Badge>
              <Badge className="bg-gray-200 text-gray-900">
                Promo Code: {summary.methodTotals["PROMO_CODE"] || 0}
              </Badge>
            </div>
          </div>
        </div>
        {/* Table Section */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <div className="flex items-center">
                    <Calendar className="mr-2 h-4 w-4" />
                    Date
                  </div>
                </TableHead>
                <TableHead>User</TableHead>
                <TableHead>Redemption Method</TableHead>
                <TableHead className="text-right">Points</TableHead>
                <TableHead>Code/Payment</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mockRedemptions.length ? (
                mockRedemptions.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{new Date(r.date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{r.username}</div>
                        <div className="text-xs text-gray-500">{r.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        {getTypeIcon(r.type)}
                        <span className="ml-2">
                          {r.type === "CASH"
                            ? "Cash"
                            : r.type === "GIFT_CARD"
                            ? "Gift Card"
                            : "Promo Code"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold">{r.pointValueAtRedemption}</TableCell>
                    <TableCell>
                      {r.type === "CASH"
                        ? r.paymentMethod || "N/A"
                        : r.code
                        ? r.code
                        : "N/A"}
                    </TableCell>
                    <TableCell>{getStatusBadge(r.status)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-4 text-gray-500">
                    No redeemed points records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default PointsRedemptionReport;
